import { useQuery } from "@tanstack/react-query";
import { hierarchy, tree, type HierarchyPointNode } from "d3-hierarchy";
import { select } from "d3-selection";
import {
  zoom,
  zoomIdentity,
  type ZoomBehavior,
  type ZoomTransform,
} from "d3-zoom";
import { Maximize2, Minus, Plus, RotateCcw, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { scenarios as scenariosApi } from "@/lib/api";
import type { ApiScenario, ApiScenarioNode, Impact } from "@/lib/api";
import { getDomainHints } from "@/lib/domain-hints";
import { useWorkspaces } from "@/store/workspaces";

/**
 * LiveScenarioTreeCanvas — renders a saved scenario as a horizontal tree
 * with pan/zoom (d3-zoom) and click-to-pin detail inspector.
 */

const IMPACT_COLOR: Record<Impact, string> = {
  low: "#22c55e",
  medium: "#f59e0b",
  high: "#ef4444",
  critical: "#7f1d1d",
};

const KIND_COLOR: Record<ApiScenarioNode["kind"], string> = {
  initial: "#e55a3c",
  state: "#3b82f6",
  event: "#a855f7",
  end: "#64748b",
};

const NODE_W = 200;
const NODE_H = 52;
const X_GAP = 240;
const Y_GAP = 70;

interface NodeDatum {
  raw: ApiScenarioNode;
}

function toHierarchy(node: ApiScenarioNode): NodeDatum & { children?: NodeDatum[] } {
  return {
    raw: node,
    children: node.children?.length
      ? node.children.map((c) => toHierarchy(c))
      : undefined,
  };
}

export function LiveScenarioTreeCanvas({ workspaceId }: { workspaceId: string }) {
  const list = useQuery({
    queryKey: ["scenarios", workspaceId],
    queryFn: () => scenariosApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });

  const [pickedId, setPickedId] = useState<string | null>(null);

  useEffect(() => {
    const arr = list.data;
    if (!arr || arr.length === 0) return;
    if (pickedId && arr.some((s) => s.id === pickedId)) return;
    const sorted = [...arr].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    setPickedId(sorted[0].id);
  }, [list.data, pickedId]);

  const picked: ApiScenario | undefined = useMemo(
    () => list.data?.find((s) => s.id === pickedId),
    [list.data, pickedId],
  );

  return (
    <div className="relative h-full w-full overflow-hidden rounded-md bg-[oklch(0.14_0.01_260)] flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-surface/60 text-[11px]">
        <div className="flex items-center gap-3 text-foreground/80">
          <span className="text-muted-foreground">Scenario</span>
          <select
            className="rounded-md border border-border bg-surface px-2 py-1 text-[11px] text-foreground/90"
            value={pickedId ?? ""}
            onChange={(e) => setPickedId(e.target.value || null)}
          >
            <option value="">— pick a scenario —</option>
            {(list.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.horizon_days}d)
              </option>
            ))}
          </select>
          {picked && (
            <span className="text-muted-foreground">
              · horizon {picked.horizon_days}d
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <Legend />
        </div>
      </div>

      {picked && picked.description && (
        <div className="px-3 py-2 border-b border-border text-[12px] text-foreground/85 bg-surface/30">
          <span className="text-muted-foreground">Initial event:</span> {picked.initial_event}
          <div className="text-muted-foreground mt-1 text-[11px]">{picked.description}</div>
        </div>
      )}

      <div className="relative flex-1 min-h-0">
        {!picked ? (
          <Empty hasAny={(list.data ?? []).length > 0} workspaceId={workspaceId} />
        ) : (
          <Tree scenario={picked} />
        )}
      </div>
    </div>
  );
}

function Tree({ scenario }: { scenario: ApiScenario }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const viewportRef = useRef<SVGGElement | null>(null);
  const zoomBehaviorRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  const [size, setSize] = useState({ w: 1000, h: 600 });
  const [transform, setTransform] = useState<ZoomTransform>(zoomIdentity);
  const [hover, setHover] = useState<NodeDatum | null>(null);
  const [selected, setSelected] = useState<HierarchyPointNode<NodeDatum> | null>(null);

  // Track container size
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ w: Math.max(300, width), h: Math.max(300, height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Lay out the tree with fixed per-node spacing so the canvas can be larger
  // than the viewport — zoom/pan handles overflow.
  const laid: HierarchyPointNode<NodeDatum> | null = useMemo(() => {
    if (!scenario) return null;
    const root = hierarchy(toHierarchy(scenario.tree));
    const layout = tree<NodeDatum>().nodeSize([Y_GAP, X_GAP]);
    return layout(root);
  }, [scenario]);

  // Bounding box for the laid-out tree
  const bounds = useMemo(() => {
    if (!laid) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;
    laid.descendants().forEach((d) => {
      // d.y = horizontal, d.x = vertical (horizontal tree)
      if (d.y < minX) minX = d.y;
      if (d.y > maxX) maxX = d.y;
      if (d.x < minY) minY = d.x;
      if (d.x > maxY) maxY = d.x;
    });
    const pad = 80;
    return {
      minX: minX - pad,
      maxX: maxX + pad + NODE_W,
      minY: minY - pad,
      maxY: maxY + pad + NODE_H,
    };
  }, [laid]);

  // Wire up d3-zoom on the SVG
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = select<SVGSVGElement, unknown>(svgRef.current);
    const z = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.2, 4])
      .filter((event) => {
        // Don't start a pan from inside a node — that's a click
        const target = event.target as Element | null;
        if (target?.closest("[data-node]")) return event.type === "wheel";
        return !event.ctrlKey && event.type !== "dblclick";
      })
      .on("zoom", (event) => setTransform(event.transform));
    svg.call(z);
    zoomBehaviorRef.current = z;
    return () => {
      svg.on(".zoom", null);
      zoomBehaviorRef.current = null;
    };
  }, []);

  // Fit-to-screen: compute transform that fits bounds into size
  const fit = useCallback(() => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const bw = bounds.maxX - bounds.minX;
    const bh = bounds.maxY - bounds.minY;
    if (bw <= 0 || bh <= 0) return;
    const scale = Math.min(size.w / bw, size.h / bh, 1);
    const tx = (size.w - bw * scale) / 2 - bounds.minX * scale;
    const ty = (size.h - bh * scale) / 2 - bounds.minY * scale;
    const next = zoomIdentity.translate(tx, ty).scale(scale);
    select(svgRef.current).call(zoomBehaviorRef.current.transform, next);
  }, [bounds, size.w, size.h]);

  // Auto-fit on first render and when the scenario changes
  useEffect(() => {
    setSelected(null);
    // Delay a frame so ResizeObserver fired at least once
    const id = requestAnimationFrame(() => fit());
    return () => cancelAnimationFrame(id);
  }, [scenario.id, fit]);

  const zoomBy = (factor: number) => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    select(svgRef.current).call(zoomBehaviorRef.current.scaleBy, factor);
  };
  const reset = () => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    select(svgRef.current).call(zoomBehaviorRef.current.transform, zoomIdentity);
  };

  if (!laid) return null;

  // Compute parent chain for selected node for breadcrumb
  const ancestors = selected ? selected.ancestors().reverse().slice(0, -1) : [];

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden">
      <svg
        ref={svgRef}
        width={size.w}
        height={size.h}
        className="cursor-grab active:cursor-grabbing select-none"
        onClick={(e) => {
          // Click on empty SVG background clears selection
          if (e.target === svgRef.current) setSelected(null);
        }}
      >
        <g
          ref={viewportRef}
          transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}
        >
          {/* Edges */}
          {laid.descendants().map((d) => {
            if (!d.parent) return null;
            const sx = d.parent.y + NODE_W / 2;
            const sy = d.parent.x + NODE_H / 2;
            const tx = d.y - NODE_W / 2;
            const ty = d.x + NODE_H / 2;
            const cx1 = (sx + tx) / 2;
            const path = `M ${sx} ${sy} C ${cx1} ${sy}, ${cx1} ${ty}, ${tx} ${ty}`;
            const p = d.data.raw.probability;
            const isOnSelectedPath =
              selected && selected.ancestors().some((a) => a.data.raw.id === d.data.raw.id);
            return (
              <g key={d.data.raw.id}>
                <path
                  d={path}
                  fill="none"
                  stroke={isOnSelectedPath ? "oklch(0.75 0.13 250)" : "oklch(0.45 0.015 260)"}
                  strokeWidth={Math.max(1, p * 3.5)}
                  strokeOpacity={isOnSelectedPath ? 0.95 : 0.7}
                />
                <text
                  x={(sx + tx) / 2}
                  y={(sy + ty) / 2 - 6}
                  textAnchor="middle"
                  fontSize="10"
                  fill="oklch(0.72 0.01 250)"
                  pointerEvents="none"
                >
                  {(p * 100).toFixed(0)}%
                </text>
              </g>
            );
          })}

          {/* Nodes */}
          {laid.descendants().map((d) => {
            const node = d.data.raw;
            const color =
              node.kind === "end" && node.impact
                ? IMPACT_COLOR[node.impact]
                : KIND_COLOR[node.kind];
            const isHover = hover?.raw.id === node.id;
            const isSelected = selected?.data.raw.id === node.id;
            const accent = isSelected
              ? "oklch(0.85 0.12 250)"
              : color;
            return (
              <g
                key={node.id}
                data-node
                transform={`translate(${d.y - NODE_W / 2}, ${d.x})`}
                onMouseEnter={() => setHover(d.data)}
                onMouseLeave={() => setHover(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected((cur) => (cur?.data.raw.id === node.id ? null : d));
                }}
                style={{ cursor: "pointer" }}
              >
                <rect
                  x={0}
                  y={0}
                  width={NODE_W}
                  height={NODE_H}
                  rx={7}
                  fill={`color-mix(in oklab, ${color} ${isSelected ? "28%" : "18%"}, oklch(0.18 0.01 260))`}
                  stroke={accent}
                  strokeWidth={isSelected ? 2.5 : isHover ? 2 : 1.5}
                />
                {node.impact && (
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={NODE_H}
                    rx={1}
                    fill={IMPACT_COLOR[node.impact]}
                  />
                )}
                <text
                  x={NODE_W / 2}
                  y={20}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="600"
                  fill="oklch(0.95 0.005 260)"
                >
                  {clip(node.label, 28)}
                </text>
                <text
                  x={NODE_W / 2}
                  y={38}
                  textAnchor="middle"
                  fontSize="10"
                  fill="oklch(0.68 0.01 250)"
                >
                  {node.kind}
                  {node.impact ? ` · ${node.impact} impact` : ""}
                  {" · "}
                  {(node.probability * 100).toFixed(0)}%
                </text>
              </g>
            );
          })}
        </g>
      </svg>

      {/* Zoom controls */}
      <div className="absolute left-3 bottom-3 z-30 flex flex-col gap-1 rounded-md border border-border bg-surface/90 backdrop-blur p-1 shadow-lg">
        <ZoomButton onClick={() => zoomBy(1.4)} title="Zoom in">
          <Plus className="h-3.5 w-3.5" />
        </ZoomButton>
        <ZoomButton onClick={() => zoomBy(0.7)} title="Zoom out">
          <Minus className="h-3.5 w-3.5" />
        </ZoomButton>
        <ZoomButton onClick={fit} title="Fit to screen">
          <Maximize2 className="h-3.5 w-3.5" />
        </ZoomButton>
        <ZoomButton onClick={reset} title="Reset (100%)">
          <RotateCcw className="h-3.5 w-3.5" />
        </ZoomButton>
        <div className="text-[9px] font-mono text-muted-foreground text-center pt-0.5">
          {(transform.k * 100).toFixed(0)}%
        </div>
      </div>

      {/* Hover quick tooltip — only when nothing pinned */}
      {hover && !selected && (
        <div className="absolute right-3 top-3 z-30 max-w-xs rounded-md border border-border bg-popover px-3 py-2 text-[11px] shadow-xl pointer-events-none">
          <div className="font-semibold text-foreground mb-0.5">{hover.raw.label}</div>
          <div className="text-muted-foreground text-[10px] uppercase tracking-wider">
            {hover.raw.kind}
            {hover.raw.impact ? ` · ${hover.raw.impact} impact` : ""}
            {" · "}
            {(hover.raw.probability * 100).toFixed(0)}%
          </div>
        </div>
      )}

      {/* Pinned detail panel — click a node */}
      {selected && (
        <DetailPanel
          node={selected}
          ancestors={ancestors}
          onClose={() => setSelected(null)}
        />
      )}

      <div className="absolute right-3 bottom-3 z-20 text-[10px] text-muted-foreground font-mono bg-surface/70 backdrop-blur px-2 py-1 rounded">
        scroll to zoom · drag to pan · click node for details
      </div>
    </div>
  );
}

function DetailPanel({
  node,
  ancestors,
  onClose,
}: {
  node: HierarchyPointNode<NodeDatum>;
  ancestors: HierarchyPointNode<NodeDatum>[];
  onClose: () => void;
}) {
  const n = node.data.raw;
  const color = n.kind === "end" && n.impact ? IMPACT_COLOR[n.impact] : KIND_COLOR[n.kind];
  // Compound probability along the path from root
  const cumulative = node.ancestors().reduce((acc, a) => acc * a.data.raw.probability, 1);
  const childCount = n.children?.length ?? 0;
  return (
    <div className="absolute right-3 top-3 bottom-3 z-30 w-[340px] rounded-md border border-border bg-popover shadow-2xl flex flex-col">
      <div className="flex items-start justify-between gap-2 p-3 border-b border-border">
        <div className="flex items-start gap-2 min-w-0">
          <span
            className="mt-1 h-2.5 w-2.5 rounded-full shrink-0"
            style={{ background: color, boxShadow: `0 0 8px ${color}` }}
          />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {n.kind}
              {n.impact ? ` · ${n.impact} impact` : ""}
            </div>
            <div className="text-[14px] font-semibold text-foreground leading-snug">
              {n.label}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground shrink-0"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-3 text-[12px]">
        {ancestors.length > 0 && (
          <div>
            <div className="label-eyebrow !mb-1">Path</div>
            <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-1.5 gap-y-0.5">
              {ancestors.map((a, i) => (
                <span key={a.data.raw.id} className="flex items-center gap-1.5">
                  <span className="text-foreground/85">{clip(a.data.raw.label, 24)}</span>
                  {i < ancestors.length - 1 && <span className="text-muted-foreground">›</span>}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Stat label="Branch probability" value={`${(n.probability * 100).toFixed(0)}%`} />
          <Stat
            label="Compound from root"
            value={`${(cumulative * 100).toFixed(1)}%`}
            hint="P(this node) × all ancestor branches"
          />
          {n.impact && <Stat label="Impact" value={n.impact} color={IMPACT_COLOR[n.impact]} />}
          <Stat label="Children" value={String(childCount)} />
        </div>

        {n.summary && (
          <div>
            <div className="label-eyebrow !mb-1">Summary</div>
            <p className="text-foreground/90 leading-relaxed">{n.summary}</p>
          </div>
        )}

        {childCount > 0 && (
          <div>
            <div className="label-eyebrow !mb-1">Branches from here</div>
            <ul className="space-y-1">
              {n.children!.map((c) => (
                <li
                  key={c.id}
                  className="flex items-start gap-2 text-[11px] text-foreground/85"
                >
                  <span
                    className="mt-1 h-1.5 w-1.5 rounded-full shrink-0"
                    style={{
                      background:
                        c.kind === "end" && c.impact
                          ? IMPACT_COLOR[c.impact]
                          : KIND_COLOR[c.kind],
                    }}
                  />
                  <span className="flex-1">{c.label}</span>
                  <span className="font-mono text-muted-foreground">
                    {(c.probability * 100).toFixed(0)}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  color,
  hint,
}: {
  label: string;
  value: string;
  color?: string;
  hint?: string;
}) {
  return (
    <div className="rounded border border-border bg-surface-elevated/40 px-2 py-1.5" title={hint}>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div
        className="font-mono text-[13px] font-semibold mt-0.5"
        style={{ color: color ?? "oklch(0.95 0.005 260)" }}
      >
        {value}
      </div>
    </div>
  );
}

function ZoomButton({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="grid place-items-center h-6 w-6 rounded text-muted-foreground hover:text-foreground hover:bg-surface-elevated transition"
    >
      {children}
    </button>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-3">
      <LegendDot color={KIND_COLOR.initial} label="initial" />
      <LegendDot color={KIND_COLOR.state} label="state" />
      <LegendDot color={KIND_COLOR.event} label="event" />
      <LegendDot color={IMPACT_COLOR.high} label="end (impact)" />
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

function Empty({ hasAny, workspaceId }: { hasAny: boolean; workspaceId?: string }) {
  const { getById } = useWorkspaces();
  const ws = workspaceId ? getById(workspaceId) : undefined;
  const example = getDomainHints(ws?.domain).scenarioExample;
  return (
    <div className="absolute inset-0 grid place-items-center text-center px-6">
      <div className="max-w-md">
        <div className="label-eyebrow mb-2">{hasAny ? "Pick a scenario" : "No scenarios yet"}</div>
        <h3 className="text-[15px] font-semibold mb-2">
          {hasAny ? "Select one from the dropdown" : "Generate one via chat"}
        </h3>
        {!hasAny && (
          <p className="text-[12px] text-muted-foreground">
            Try: <span className="text-foreground/85 italic">{example}</span>
          </p>
        )}
      </div>
    </div>
  );
}

function clip(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
